import type { ServiceAccount } from '@compass/client/types/service_account';
import { useServiceAccounts, useUpdateServiceAccount } from '@hooks/useServiceAccounts';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, KeyRound, MoreHorizontal, Plus } from 'lucide-react';
import moment from 'moment';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { CreateServiceAccountDialog } from './CreateServiceAccountDialog';
import { DeleteServiceAccountDialog } from './DeleteServiceAccountDialog';
import { EditServiceAccountDialog } from './EditServiceAccountDialog';
import { RotateServiceAccountDialog } from './RotateServiceAccountDialog';
import { formatDate, formatRelative } from './settingsFormatting';

type RowDialog = 'edit' | 'rotate' | 'delete';

function getStatus(sa: ServiceAccount): {
  label: string;
  variant: 'success' | 'warning' | 'destructive' | 'secondary';
} {
  if (sa.disabled) return { label: 'Disabled', variant: 'secondary' };
  if (sa.expires_at && moment(sa.expires_at).isBefore(moment())) {
    return { label: 'Expired', variant: 'destructive' };
  }
  if (sa.expires_at && moment(sa.expires_at).diff(moment(), 'days') <= 7) {
    return { label: 'Expiring soon', variant: 'warning' };
  }
  return { label: 'Active', variant: 'success' };
}

export function ServiceAccountsSection() {
  const { data, isLoading, isError, error } = useServiceAccounts(true);
  const updateMutation = useUpdateServiceAccount();

  const [createOpen, setCreateOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<{
    sa: ServiceAccount;
    dialog: RowDialog;
  } | null>(null);

  const serviceAccounts = data?.service_accounts ?? [];

  const handleToggleDisabled = (sa: ServiceAccount) => {
    if (!sa.id) return;
    const nextDisabled = !sa.disabled;
    updateMutation.mutate(
      { service_account_id: sa.id, disabled: nextDisabled },
      {
        onSuccess: () => {
          toast.success(nextDisabled ? 'Service account disabled' : 'Service account enabled');
        },
        onError: err => {
          toast.error(getErrorMessage(err) || 'Failed to update service account');
        },
      },
    );
  };

  return (
    <>
      <div className="mt-6 rounded-lg border bg-card p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
              <KeyRound className="h-6 w-6 text-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Service Accounts</h3>
              <p className="text-sm text-muted-foreground">
                Credentials for automating against the Compass API. Inherit your permissions; the
                secret is shown only once.
              </p>
            </div>
          </div>
          <Button size="sm" className="shrink-0" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New
          </Button>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isError ? (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">Failed to load service accounts</p>
                  <p className="mt-1">{getErrorMessage(error) || 'Please try again later.'}</p>
                </div>
              </div>
            </div>
          ) : serviceAccounts.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/30 px-6 py-8 text-center">
              <p className="text-sm font-medium">No service accounts yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create one to authenticate automation (CI, scripts, integrations) against the
                Compass API.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create service account
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Access Key ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serviceAccounts.map(sa => {
                    const status = getStatus(sa);
                    return (
                      <TableRow key={sa.id}>
                        <TableCell>
                          <div className="font-medium">{sa.name || '—'}</div>
                          {sa.description && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {sa.description}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <code className="text-xs font-mono text-muted-foreground">
                              {sa.access_key_id}
                            </code>
                            {sa.access_key_id && (
                              <CopyButton value={sa.access_key_id} label="Access key ID" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatRelative(sa.last_used_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(sa.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {sa.expires_at ? formatDate(sa.expires_at) : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => setActiveRow({ sa, dialog: 'edit' })}
                              >
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => setActiveRow({ sa, dialog: 'rotate' })}
                              >
                                Rotate secret
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => handleToggleDisabled(sa)}
                                disabled={updateMutation.isPending}
                              >
                                {sa.disabled ? 'Enable' : 'Disable'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => setActiveRow({ sa, dialog: 'delete' })}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <CreateServiceAccountDialog open={createOpen} onOpenChange={setCreateOpen} />

      {activeRow?.dialog === 'edit' && (
        <EditServiceAccountDialog
          open
          onOpenChange={next => {
            if (!next) setActiveRow(null);
          }}
          serviceAccount={activeRow.sa}
        />
      )}

      {activeRow?.dialog === 'rotate' && (
        <RotateServiceAccountDialog
          open
          onOpenChange={next => {
            if (!next) setActiveRow(null);
          }}
          serviceAccount={activeRow.sa}
        />
      )}

      {activeRow?.dialog === 'delete' && (
        <DeleteServiceAccountDialog
          open
          onOpenChange={next => {
            if (!next) setActiveRow(null);
          }}
          serviceAccount={activeRow.sa}
        />
      )}
    </>
  );
}
